import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";

/**
 * One script attributed inside a Long Animation Frame entry. The Long Animation Frame API
 * (Chrome 123+) is not in lib.dom, so its shape is named here.
 */
type LongAnimationFrameScript = {
  invoker?: string;
  invokerType?: string;
  sourceURL?: string;
  sourceFunctionName?: string;
  sourceCharPosition?: number;
  duration: number;
  executionStart: number;
};

type LongAnimationFrameEntry = {
  duration: number;
  /** The four timing members below are read into the Sentry `extra` payload further down. */
  blockingDuration?: number;
  firstUIEventTimestamp?: number;
  renderStart?: number;
  styleAndLayoutStart?: number;
  scripts?: LongAnimationFrameScript[];
};

/**
 * The two non-standard memory APIs this entry point probes for. `measureUserAgentSpecificMemory`
 * needs cross-origin isolation (Chrome 89+); `memory` is the older Chrome-only fallback. Neither is
 * declared on lib.dom's `Performance`, and both are feature-detected before use.
 */
type PerformanceWithMemoryApis = Performance & {
  measureUserAgentSpecificMemory?: () => Promise<{
    bytes: number;
    breakdown: Array<{
      bytes: number;
      types: string[];
      attribution?: Array<{ url?: string; scope?: string }>;
    }>;
  }>;
  memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number; totalJSHeapSize?: number };
};
import "./utils/slickgridPatches";
import { BrowserRouter } from "react-router-dom";
import { registerLicense } from "@syncfusion/ej2-base";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import * as Sentry from "@sentry/react";
import { sentryBeforeSend } from "../sentryConstant";
import { injectScripts } from "./../injectScripts";
import { getBackendUrl } from "@Utils";
import { initPostHog } from "./services/posthog";

injectScripts();
initPostHog();

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

registerLicense(process.env.REACT_APP_LICENSE_KEY as string);

if (process.env.REACT_APP_SENTRY_DSN) {
  const backendUrl = getBackendUrl();
  // CP-283 recipient pages: no Session Replay. Replay here runs unmasked (maskAllText:false,
  // blockAllMedia:false) with replaysOnErrorSampleRate 1.0, which on any error would upload a pixel
  // record of a STRANGER viewing shared project content — plus the token-bearing URL — to Sentry
  // SaaS. Share views always start as a full document load on /share/..., so deciding at init time
  // is complete. Errors themselves still report (with the URL scrubbed in sentryBeforeSend).
  const isShareRecipientPage = globalThis.location.pathname.startsWith("/share/");
  Sentry.init({
    release: process.env.REACT_APP_VERSION,
    environment: process.env.REACT_APP_ENV,
    dsn: process.env.REACT_APP_SENTRY_DSN,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration({
        enableLongAnimationFrame: true,
        enableLongTask: true,
        enableInp: true,
      }),
      Sentry.browserProfilingIntegration(),
      ...(isShareRecipientPage
        ? []
        : [
            Sentry.replayIntegration({
              networkDetailAllowUrls: backendUrl ? [backendUrl] : [],
              maskAllText: false,
              blockAllMedia: false,
            }),
          ]),
    ],
    replaysOnErrorSampleRate: isShareRecipientPage ? 0 : 1.0,
    tracePropagationTargets: backendUrl ? [backendUrl] : [],
    replaysSessionSampleRate: isShareRecipientPage ? 0 : 0.1,
    tracesSampleRate: 0.05,
    profilesSampleRate: 0.05,
    beforeSend(event, hint) {
      return sentryBeforeSend(event, hint);
    },
  });
  // Track long animation frames (main thread blocking) with function attribution
  if ("PerformanceObserver" in window) {
    // Extract readable chunk/file name from a full URL
    const getFileName = (url: string | undefined) => {
      if (!url) return "";
      try {
        const path = new URL(url).pathname;
        // Return last segment, e.g. "IssuesList-ZcI2gZKf.js" from "/assets/IssuesList-ZcI2gZKf.js"
        return path.split("/").pop() || path;
      } catch {
        return url;
      }
    };

    try {
      // Long Animation Frame API (Chrome 123+) — gives function-level attribution
      const loafObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const loaf = entry as PerformanceEntry & LongAnimationFrameEntry;
          if (loaf.duration < 5000) continue; // only report >100ms blocks

          const scripts = (loaf.scripts || []).map((s) => ({
            invoker: s.invoker,
            invokerType: s.invokerType,
            sourceURL: s.sourceURL,
            sourceFunctionName: s.sourceFunctionName,
            sourceCharPosition: s.sourceCharPosition,
            duration: Math.round(s.duration),
            executionStart: Math.round(s.executionStart),
          }));

          // Find the slowest script entry (most likely culprit)
          const slowest = scripts.length ? scripts.reduce((a, b) => (a.duration >= b.duration ? a : b)) : null;

          // Build a readable blocker identifier:
          // Prefer invoker (e.g. "BUTTON.onclick") > sourceURL filename > sourceFunctionName
          const blockerFile = slowest ? getFileName(slowest.sourceURL) : "";
          const blockerInvoker = slowest?.invoker || "";
          const blockerFunc = slowest?.sourceFunctionName || "";
          const blockerLabel = blockerInvoker || blockerFile || blockerFunc || "unknown";

          // Fingerprint by blocker + page so Sentry groups same-source blocks together
          const page = window.location.pathname;

          Sentry.captureEvent({
            message: `Main thread blocked for ${Math.round(loaf.duration)}ms`,
            level: "warning",
            fingerprint: ["main-thread-blocked", blockerLabel, page],
            tags: {
              "thread.blocked_duration": Math.round(loaf.duration),
              "thread.blocker": blockerLabel,
              "thread.blocker_file": blockerFile || "N/A",
              "thread.blocker_invoker": blockerInvoker || "N/A",
              "thread.blocker_function": blockerFunc || "N/A",
              "thread.blocker_type": slowest?.invokerType || "N/A",
              "thread.blocker_char_pos": slowest?.sourceCharPosition ?? "N/A",
              "thread.scripts_count": scripts.length,
            },
            extra: {
              duration: loaf.duration,
              blockingDuration: loaf.blockingDuration,
              firstUIEventTimestamp: loaf.firstUIEventTimestamp,
              renderStart: loaf.renderStart,
              styleAndLayoutStart: loaf.styleAndLayoutStart,
              scripts,
            },
          });
        }
      });
      loafObserver.observe({ type: "long-animation-frame", buffered: true });
    } catch {
      // Long Animation Frame not supported, fall back to Long Tasks
      try {
        const ltObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration < 500) continue;
            Sentry.addBreadcrumb({
              category: "long-task",
              message: `Main thread blocked for ${Math.round(entry.duration)}ms`,
              level: "warning",
              data: { duration: entry.duration, name: entry.name },
            });
          }
        });
        ltObserver.observe({ type: "longtask", buffered: true });
      } catch {
        // longtask not supported
      }
    }
  }

  // Track memory spikes — requires CloudFront headers:
  // Cross-Origin-Opener-Policy: same-origin
  // Cross-Origin-Embedder-Policy: credentialless
  const MB = 1024 * 1024;
  let lastMemoryBytes = 0;

  if (window.crossOriginIsolated && (performance as PerformanceWithMemoryApis).measureUserAgentSpecificMemory) {
    // Per-script memory breakdown (Chrome 89+, requires cross-origin isolation)
    const measureMemory = async () => {
      try {
        const result = await (performance as PerformanceWithMemoryApis).measureUserAgentSpecificMemory!();
        const totalMB = result.bytes / MB;
        const deltaMB = (result.bytes - lastMemoryBytes) / MB;

        const breakdown = result.breakdown
          .filter((entry) => entry.bytes > 0)
          .map((entry) => ({
            bytes: entry.bytes,
            mb: Math.round(entry.bytes / MB),
            types: entry.types,
            urls: entry.attribution?.map((a) => a.url || a.scope) || [],
          }));

        Sentry.addBreadcrumb({
          category: "memory",
          message: `Heap: ${Math.round(totalMB)}MB (${deltaMB > 0 ? "+" : ""}${Math.round(deltaMB)}MB)`,
          level: "info",
          data: { totalMB: Math.round(totalMB), deltaMB: Math.round(deltaMB) },
        });

        // eslint-disable-next-line no-constant-condition, no-constant-binary-expression -- alerting intentionally disabled pending threshold tuning
        if (false && (totalMB > 4000 || deltaMB > 4000)) {
          Sentry.captureEvent({
            message: `Memory spike: ${Math.round(totalMB)}MB (${deltaMB > 0 ? "+" : ""}${Math.round(deltaMB)}MB)`,
            level: "warning",
            tags: {
              "memory.total_mb": Math.round(totalMB),
              "memory.delta_mb": Math.round(deltaMB),
              "memory.page": window.location.pathname,
            },
            extra: { totalBytes: result.bytes, breakdown },
          });
        }

        lastMemoryBytes = result.bytes;
      } catch {
        // measurement failed
      }
      // Poll with 30s + random jitter (0-5s)
      setTimeout(measureMemory, 30000 + Math.random() * 5000);
    };
    setTimeout(measureMemory, 5000); // start after 5s
  } else if ((performance as PerformanceWithMemoryApis).memory) {
    // Fallback: Chrome performance.memory (no per-script breakdown)
    const checkMemory = () => {
      const mem = (performance as PerformanceWithMemoryApis).memory!;
      const usedMB = mem.usedJSHeapSize / MB;
      const limitMB = mem.jsHeapSizeLimit / MB;
      const deltaMB = (mem.usedJSHeapSize - lastMemoryBytes) / MB;
      const pct = Math.round((mem.usedJSHeapSize / mem.jsHeapSizeLimit) * 100);

      Sentry.addBreadcrumb({
        category: "memory",
        message: `Heap: ${Math.round(usedMB)}MB / ${Math.round(limitMB)}MB (${pct}%)`,
        level: "info",
        data: { usedMB: Math.round(usedMB), limitMB: Math.round(limitMB), pct },
      });

      // eslint-disable-next-line no-constant-condition, no-constant-binary-expression -- alerting intentionally disabled pending threshold tuning
      if (false && (usedMB > 4000 || deltaMB > 4000 || pct > 80)) {
        Sentry.captureEvent({
          message: `Memory spike: ${Math.round(usedMB)}MB / ${Math.round(limitMB)}MB (${pct}%)`,
          level: "warning",
          tags: {
            "memory.total_mb": Math.round(usedMB),
            "memory.pct": pct,
            "memory.page": window.location.pathname,
          },
          extra: {
            usedJSHeapSize: mem.usedJSHeapSize,
            totalJSHeapSize: mem.totalJSHeapSize,
            jsHeapSizeLimit: mem.jsHeapSizeLimit,
            deltaMB: Math.round(deltaMB),
          },
        });
      }

      lastMemoryBytes = mem.usedJSHeapSize;
      setTimeout(checkMemory, 30000 + Math.random() * 5000);
    };
    setTimeout(checkMemory, 5000);
  }
}

root.render(
  // <React.StrictMode>
  <BrowserRouter>
    <App />
  </BrowserRouter>,
  // </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
